/**
 * Per-browser persistence of the in-flight WebAuthn enrollment request_id.
 *
 * Scope: localStorage is per-origin, per-browser-profile — the same effective
 * scope as a platform passkey (bound to this browser's authenticator). There is
 * no web API for cross-browser / physical-device storage, so this is the
 * correct granularity for tracking "this browser's registration".
 *
 * One key per analyst so multiple analysts on the same browser don't collide.
 * A single key means a new request always REPLACES the previous id — there is
 * never a list of stale ids to leak into future status checks.
 *
 * The value is AES-encrypted at rest (crypto-js) so the id isn't readable in
 * devtools, matching how the app obfuscates other client-stored data.
 * ⚠️ This is obfuscation, NOT security: the key ships in the bundle. The
 * request_id is an opaque, non-secret handle anyway — encryption is only to
 * avoid casual inspection / editing.
 */
import CryptoJS from "crypto-js";

const KEY = "stepout-webauthn-reqid-v1-Zx4kQe";
const keyFor = (analystId) => `webauthn_reg_request_${analystId}`;

export function getRegRequestId(analystId) {
  if (analystId == null) return null;
  try {
    const raw = localStorage.getItem(keyFor(analystId));
    if (!raw) return null;
    const plain = CryptoJS.AES.decrypt(raw, KEY).toString(CryptoJS.enc.Utf8);
    return plain || null; // empty => wrong key / tampered / legacy plaintext
  } catch {
    return null; // storage blocked or undecryptable
  }
}

export function setRegRequestId(analystId, requestId) {
  if (analystId == null || !requestId) return;
  try {
    const cipher = CryptoJS.AES.encrypt(String(requestId), KEY).toString();
    localStorage.setItem(keyFor(analystId), cipher);
  } catch {
    /* non-fatal */
  }
}

export function clearRegRequestId(analystId) {
  if (analystId == null) return;
  try {
    localStorage.removeItem(keyFor(analystId));
  } catch {
    /* non-fatal */
  }
}
